import { Link, useMatches, useRouterState } from "@tanstack/react-router";
import { Menu } from "lucide-react";

import { useApi } from "@/api/hooks";
import type { NavGroup } from "@/app/registry";
import { cn } from "@/lib/cn";
import { sectionFor } from "@/pages/admin/sections";

import { isConsolePath } from "./consolePages";
import { highlighted, sectionOf, useSectionLabels, useSections } from "./sections";

/** "Market open" / "Market closed", from the ticker the shell already polls. */
export function MarketStatus({ className }: { className?: string }) {
  const q = useApi("/live/ticker", undefined, {
    refetchInterval: 2_000,
    staleTime: 2_000,
  });
  if (!q.data) return null;
  const live = q.data.live;
  return (
    <span
      className={cn(
        "hidden items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap xl:inline-flex",
        live ? "bg-up-soft text-up" : "bg-surface-3 text-muted",
        className,
      )}
    >
      <span
        className={cn(
          "size-[7px] rounded-full bg-current",
          live && "animate-pulse-dot",
        )}
      />
      {live ? "Market open" : "Market closed"}
    </span>
  );
}

/** "Markets / Option chain", "Admin console / Users". */
export function Breadcrumb({ className }: { className?: string }) {
  const sections = useSections();
  const labels = useSectionLabels();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const matches = useMatches();
  const title = [...matches].reverse().find((m) => m.staticData?.title)
    ?.staticData.title;
  let section: string | undefined;
  let page = title;
  if (isConsolePath(path)) {
    section = "Admin console";
    page = sectionFor(path).label;
  } else {
    const g = sectionOf(sections, path);
    section = g ? labels[g as NavGroup] : undefined;
  }
  if (!page) return null;
  return (
    <div className={cn("min-w-0 truncate text-[13px] text-muted", className)}>
      {section && section !== page ? <>{section} / </> : null}
      <b className="font-semibold text-fg">{page}</b>
    </div>
  );
}

/** Phones: the first page of up to four sections, and the full menu. */
export function BottomBar({ onMenu }: { onMenu: () => void }) {
  const sections = useSections();
  const labels = useSectionLabels();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const current = isConsolePath(path) ? "System" : sectionOf(sections, path);
  const tabs = [...sections.entries()].slice(0, 4);
  return (
    <nav
      aria-label="Sections"
      className="safe-bottom fixed inset-x-0 bottom-0 z-30 grid border-t border-border bg-surface/95 pr-[var(--safe-right)] pl-[var(--safe-left)] backdrop-blur lg:hidden"
      style={{
        gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))`,
      }}
    >
      {tabs.map(([group, entries]) => {
        const first = entries[0];
        if (!first) return null;
        const Icon = first.nav.icon;
        const on = group === current;
        return (
          <Link
            key={group}
            to={first.path}
            className={cn(
              "grid justify-items-center gap-0.5 pt-2 pb-1.5 text-[10.5px] font-semibold",
              on ? "text-brand" : "text-subtle",
            )}
          >
            <Icon
              className={cn(
                "size-5",
                !on && highlighted(entries) && "text-[#6d4fd8] dark:text-[#b39dff]",
              )}
            />
            <span className="max-w-full truncate px-1">
              {/* the "<brand> AI" section is just "AI" in the narrow tab, "Premium tools" just "Premium" */}
              {highlighted(entries)
                ? "AI"
                : group === "Premium"
                  ? "Premium"
                  : labels[group as NavGroup]}
            </span>
          </Link>
        );
      })}
      <button
        type="button"
        onClick={onMenu}
        className="grid justify-items-center gap-0.5 pt-2 pb-1.5 text-[10.5px] font-semibold text-subtle"
      >
        <Menu className="size-5" />
        <span>Menu</span>
      </button>
    </nav>
  );
}
